import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';
import Login from './content/pages/Auth/Login';
// This import is needed but TypeScript doesn't recognize it properly
import RtuRegistrationPage from './content/pages/Auth/Register/RtuRegistrationPage';
import ForgotPasswordPage from './content/pages/Auth/ForgotPassword';
import ResetPasswordPage from './content/pages/Auth/ResetPassword';
import InvitationAcceptance from './content/pages/Auth/Register/InvitationAcceptance';
import _DashboardComponent from './content/dashboards/Dashboard';
import { Protection } from './components/Protection';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import { useAuth } from 'src/contexts/AuthContext';
import BillingItemsPage from './pages/BillingItemsPage';
import BillingInvoicingPage from 'src/features/billing-invoicing/pages/BillingInvoicingPage';
import { ReportsProvider } from './features/reports/context/ReportsContext';
import { DispositionCalendarProvider } from './contexts/DispositionCalendarContext';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Auth
const LoginRoute = () => {
  const auth = useAuth();

  // If auth is still loading, show loader
  if (auth.loading) {
    return <SuspenseLoader />;
  }

  // Check if user is authenticated
  if (auth.isAuthenticated()) {
    return <Navigate to="/dashboards/main" replace />;
  }

  // Otherwise, render the Login component
  return <Login />;
};

// Dashboards
const Dashboard = Loader(
  lazy(() => import('src/content/dashboards/Dashboard'))
);

// Disposition
const Calendar = Loader(lazy(() => import('src/content/disposition/Calendar')));

// Requests
const RequestListPage = Loader(
  lazy(() => import('src/pages/Requests/RequestListPage'))
);
const RequestDetailPage = Loader(
  lazy(() => import('src/pages/Requests/RequestDetailPage'))
);
const CreateRequestPage = Loader(
  lazy(() => import('src/pages/Requests/CreateRequestPage'))
);
const RequestEditPage = Loader(
  lazy(() => import('src/pages/Requests/RequestEditPage'))
);

// Admin
const PendingRegistrations = Loader(
  lazy(() => import('src/content/pages/Admin/Registrations'))
);
const UserInvitations = Loader(
  lazy(() => import('src/content/pages/Admin/Invitations'))
);
const UserManagement = Loader(
  lazy(() => import('src/content/pages/Admin/Users'))
);

// Infrastructure
const ManageInfrastructure = Loader(
  lazy(() => import('src/content/infrastructure/Manage'))
);

// Status
const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

// Add import for the profile page
const ProfilePage = Loader(lazy(() => import('src/content/pages/Profile')));

// Tariffs
const TariffsPage = Loader(lazy(() => import('src/pages/TariffsPage')));
const ServiceTypeMatchingRulesPage = Loader(
  lazy(() => import('src/features/tariffs/pages/ServiceTypeMatchingRulesPage'))
);

// Journey Progression
const JourneyProgressionPage = Loader(
  lazy(() => import('src/features/disposition/pages/JourneyProgressionPage'))
);

// Simulate Train Ops
const SimulateTrainOps = Loader(lazy(() => import('./pages/SimulateTrainOps')));

// TrackDisplayPage
const TrackDisplayPage = Loader(
  lazy(() => import('src/pages/Infrastructure/TrackDisplayPage'))
);

// Reports
const ReportsPage = Loader(
  lazy(() => import('src/features/reports/pages/ReportsPage'))
);

// Train List
const TrainListPage = Loader(lazy(() => import('src/pages/TrainListPage')));

// AccountingProtectedRoute has been replaced by Protection component with blockedRoles

// Review Tasks
const ReviewTasksPage = Loader(
  lazy(() => import('src/features/billing-invoicing/pages/ReviewTasksPage'))
);
const ReviewTaskDetailsPage = Loader(
  lazy(
    () => import('src/features/billing-invoicing/pages/ReviewTaskDetailsPage')
  )
);

// Define routes configuration
export const routes: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/login" replace />
      },
      {
        path: 'login',
        element: <LoginRoute />
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />
      },
      {
        path: 'register/rtu',
        element: <RtuRegistrationPage />
      },
      {
        path: 'register/invite',
        element: <InvitationAcceptance />
      },
      {
        path: 'overview',
        element: <Navigate to="/dashboards/main" replace />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboards',
    element: (
      <Protection mode="route">
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="main" replace />
      },
      {
        path: 'main',
        element: <Dashboard />
      }
    ]
  },
  {
    path: 'disposition',
    element: (
      <Protection mode="route" allowedRoles={['ADMIN', 'DISPATCHER']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="calendar" replace />
      },
      {
        path: 'calendar',
        element: (
          <DispositionCalendarProvider>
            <Calendar />
          </DispositionCalendarProvider>
        )
      },
      {
        path: 'journey/:routeId',
        element: <JourneyProgressionPage />
      },
      {
        path: 'tracks',
        element: <TrackDisplayPage />
      }
    ]
  },
  {
    path: 'requests',
    element: (
      <Protection mode="route">
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <RequestListPage /> // Direct to list page
      },
      {
        path: 'new',
        element: <CreateRequestPage />
      },
      {
        path: ':id',
        element: <RequestDetailPage />
      },
      {
        path: ':id/edit',
        element: <RequestEditPage />
      }
    ]
  },
  {
    path: 'admin',
    element: (
      <Protection mode="route" allowedRoles={['ADMIN']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="registrations/pending" replace />
      },
      {
        path: 'registrations/pending',
        element: <PendingRegistrations />
      },
      {
        path: 'invitations',
        element: <UserInvitations />
      },
      {
        path: 'users',
        element: <UserManagement />
      }
    ]
  },
  {
    path: 'infrastructure',
    element: (
      <Protection mode="route" allowedRoles={['ADMIN']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="manage" replace />
      },
      {
        path: 'manage',
        element: <ManageInfrastructure />
      }
    ]
  },
  {
    path: 'management',
    element: (
      <Protection mode="route">
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="profile" replace />
      },
      {
        path: 'profile',
        element: <ProfilePage />
      }
    ]
  },
  {
    path: 'tariffs',
    element: (
      <Protection mode="route" blockedRoles={['DISPATCHER', 'RTU']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <TariffsPage />
      },
      {
        path: 'service-type-matching-rules',
        element: <ServiceTypeMatchingRulesPage />
      }
    ]
  },
  {
    path: 'billing-items',
    element: (
      <Protection mode="route" blockedRoles={['DISPATCHER', 'RTU']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <BillingItemsPage />
      }
    ]
  },
  {
    path: 'billing-invoicing',
    element: (
      <Protection mode="route" blockedRoles={['DISPATCHER', 'RTU']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <BillingInvoicingPage />
      },
      {
        path: 'review-tasks',
        element: <ReviewTasksPage />
      },
      {
        path: 'review-tasks/:taskId',
        element: <ReviewTaskDetailsPage />
      }
    ]
  },
  {
    path: 'reports',
    element: (
      <Protection mode="route" allowedRoles={['ADMIN', 'DISPATCHER']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: (
          <ReportsProvider>
            <ReportsPage />
          </ReportsProvider>
        )
      }
    ]
  },
  {
    path: 'operations',
    element: (
      <Protection mode="route" allowedRoles={['ADMIN']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: 'simulate-train-ops',
        element: <SimulateTrainOps />
      }
    ]
  },
  {
    path: 'trains',
    element: (
      <Protection mode="route" allowedRoles={['ADMIN', 'DISPATCHER']}>
        <SidebarLayout />
      </Protection>
    ),
    children: [
      {
        path: '',
        element: <TrainListPage />
      }
    ]
  }
];

// Export named routes for better HMR compatibility
export const appRoutes = routes;

// Using a named function declaration for the default export component helps with HMR
